import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import { useStats } from '../hooks/useCMS';
import { 
  GraduationCap, 
  BookOpen, 
  Trophy, 
  Users, 
  Zap, 
  Flame,
  ArrowRight,
  Star,
  Target,
  Clock,
  TrendingUp
} from 'lucide-react';
import { 
  AnimatedCard, 
  FadeIn, 
  SlideIn, 
  FloatingElement,
  StaggerContainer,
  StaggerItem,
  ConfettiEffect
} from '../components/ui/AnimatedCard';

export function Home() {
  const { isAuthenticated, user } = useAuthStore();
  const { data: stats } = useStats();

  const features = [
    {
      icon: BookOpen,
      title: 'Chapter-wise Exams',
      description: 'Practice MCQs organized by class, subject, and chapter',
      color: 'bg-primary-100 text-primary-600',
    },
    {
      icon: Trophy,
      title: 'Live Competitions',
      description: 'Join scheduled live exams and compete with friends',
      color: 'bg-accent-100 text-accent-600',
    },
    {
      icon: Zap,
      title: 'Earn Points',
      description: 'Get points for every correct answer and unlock rewards',
      color: 'bg-secondary-100 text-secondary-600',
    },
    {
      icon: Users,
      title: 'Study Groups',
      description: 'Create or join study groups and learn together',
      color: 'bg-purple-100 text-purple-600',
    },
    {
      icon: Target,
      title: 'Track Progress',
      description: 'Monitor your improvement with detailed analytics',
      color: 'bg-rose-100 text-rose-600',
    },
    {
      icon: Star,
      title: 'Win Badges',
      description: 'Collect achievement badges and show off your skills',
      color: 'bg-amber-100 text-amber-600',
    },
  ];

  const siteStats = [
    { label: 'Active Students', value: stats?.totalUsers?.toString() || '0', icon: Users },
    { label: 'Total Exams', value: stats?.totalExams?.toString() || '0', icon: BookOpen },
    { label: 'Questions', value: stats?.totalQuestions?.toString() || '0', icon: Target },
    { label: 'Total Attempts', value: stats?.totalAttempts?.toString() || '0', icon: TrendingUp },
  ];

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-secondary-400 rounded-full blur-3xl" />
        </div>
        
        <div className="relative px-6 py-16 md:py-24 text-center">
          <FloatingElement amplitude={5}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.8 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-2xl mb-6 backdrop-blur-sm"
            >
              <GraduationCap className="w-10 h-10" />
            </motion.div>
          </FloatingElement>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-6xl font-bold mb-4"
          >
            Study Bangladesh
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl md:text-2xl text-primary-100 mb-2 font-bangla"
          >
            শেখো, প্র্যাকটিস করো, এগিয়ে যাও
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-primary-200 mb-8 max-w-2xl mx-auto"
          >
            The most student-friendly MCQ exam platform in Bangladesh. 
            Practice chapter-wise exams, compete with friends, and track your progress.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            {isAuthenticated ? (
              <Link
                to="/exams"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-600 font-bold rounded-2xl hover:bg-primary-50 transition-colors shadow-lg"
              >
                Start Practicing
                <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-600 font-bold rounded-2xl hover:bg-primary-50 transition-colors shadow-lg"
                >
                  Get Started Free
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/exams"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-500/30 text-white font-bold rounded-2xl hover:bg-primary-500/40 transition-colors backdrop-blur-sm"
                >
                  Browse Exams
                </Link>
              </>
            )}
          </motion.div>

          {/* User Stats */}
          {isAuthenticated && user && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-8 flex flex-wrap justify-center gap-4"
            >
              <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
                <Zap className="w-4 h-4 text-accent-300" />
                <span className="font-semibold">{user.points} Points</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
                <Flame className="w-4 h-4 text-orange-300" />
                <span className="font-semibold">{user.streakCount} Day Streak</span>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section>
        <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {siteStats.map((stat) => (
            <StaggerItem key={stat.label}>
              <AnimatedCard className="p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-xl mb-3">
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </AnimatedCard>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </section>

      {/* Features Section */}
      <section>
        <FadeIn className="text-center mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Why Choose Study Bangladesh?</h2>
          <p className="text-slate-500">Everything you need to ace your exams</p>
        </FadeIn>

        <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <StaggerItem key={feature.title}>
              <AnimatedCard className="p-6 h-full">
                <div className={`inline-flex items-center justify-center w-12 h-12 ${feature.color} rounded-xl mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.description}</p>
              </AnimatedCard>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </section>

      {/* How It Works */}
      <section className="bg-white rounded-3xl p-8 md:p-12">
        <FadeIn className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">How It Works</h2>
          <p className="text-slate-500">Start practicing in 3 simple steps</p>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              step: '01',
              title: 'Choose Your Class',
              description: 'Select your class and subjects from Class 1 to 12',
              icon: Target,
            },
            {
              step: '02',
              title: 'Pick an Exam',
              description: 'Browse chapter-wise exams or join live competitions',
              icon: BookOpen,
            },
            {
              step: '03',
              title: 'Practice & Improve',
              description: 'Get instant results, explanations, and track progress',
              icon: TrendingUp,
            },
          ].map((item, index) => (
            <SlideIn key={item.step} direction="up" delay={index * 0.2}>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-2xl text-xl font-bold mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.description}</p>
              </div>
            </SlideIn>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center py-12">
        <FadeIn>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-slate-500 mb-8 max-w-xl mx-auto">
            Join thousands of students who are already improving their grades with Study Bangladesh
          </p>
          <Link
            to={isAuthenticated ? '/exams' : '/register'}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-bold rounded-2xl hover:shadow-xl transition-shadow"
          >
            {isAuthenticated ? 'Start Practicing Now' : 'Create Free Account'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </FadeIn>
      </section>
    </div>
  );
}
